import React, { useState } from 'react'
import { useForm } from '@mantine/form'
import { Button, Checkbox, Stack, TextInput } from '@mantine/core'
import {
  IconBuildingStore,
  IconChecklist,
  IconId,
  IconLock,
  IconPhone,
  IconShieldCheck,
  IconStars,
  IconUser,
} from '@tabler/icons-react'
import { Dialog } from 'antd-mobile'
import useVerificationCodeTimer from '../hooks/useVerificationCodeTimer'
import { merchantApply } from '../api/merchantApplyService'

export default function MerchantApply() {
  const [loading, setLoading] = useState(false)
  const { label, startTimer, isActive } = useVerificationCodeTimer()

  const highlights = [
    {
      title: '官方认证',
      description: '悦享查官方通道，流程清晰、合规到位，让合作更放心。',
      icon: IconShieldCheck,
    },
    {
      title: '成长支持',
      description: '提供宣传材料和上手培训，遇到问题可直接找专属顾问。',
      icon: IconBuildingStore,
    },
    {
      title: '丰厚激励',
      description: '按推广业绩结算收益，回款及时、账单透明。',
      icon: IconStars,
    },
  ]

  const warmTips = [
    '请填写本人实名信息，方便后续结算和签合同。',
    '验证码可能存在延迟，如未收到请稍后重试或更换网络环境。',
    '如需帮助，可联系专属顾问：4000799168',
  ]

  const AgreementLabel = () => (
    <span className="text-sm text-gray-600">
      我已阅读并同意
      <a
        href="/merchantManual.html"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#3A55DD] font-medium"
        onClick={(e) => e.stopPropagation()}
      >
        《代理商管理手册》
      </a>
      ，承诺推广过程中不进行虚假宣传、欺骗诱导等违规行为
    </span>
  )

  const form = useForm({
    initialValues: {
      name: '',
      idCardNo: '',
      phone: '',
      smsCode: '',
      agreement: false,
    },
    validate: {
      name: (value) =>
        value === ''
          ? '请输入姓名'
          : value.length < 2 || value.length > 10
            ? '姓名长度应为2-10个字符'
            : !/^[\u4e00-\u9fa5]+$/.test(value)
              ? '姓名只能包含中文字符'
              : null,
      idCardNo: (value) =>
        value === ''
          ? '请输入身份证号'
          : !/(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(value)
            ? '请输入正确的身份证号'
            : null,
      phone: (value) => (value === '' ? '请输入手机号' : !/^1[3-9]\d{9}$/.test(value) ? '请输入正确的手机号' : null),
      smsCode: (value) => (value === '' ? '请输入验证码' : value.length !== 6 ? '验证码应为6位数字' : null),
    },
  })

  const handleSendCode = () => {
    const result = form.validateField('phone')
    if (result.hasError) {
      return
    }
    startTimer({ phone: form.values.phone, type: 16 })
  }

  const submitForm = async (values: typeof form.values) => {
    setLoading(true)
    merchantApply(values)
      .then((result) => {
        if (result.redirectUrl) {
          window.location.href = result.redirectUrl
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const handleSubmit = async (values: typeof form.values) => {
    if (!values.agreement) {
      Dialog.confirm({
        content: <AgreementLabel />,
        confirmText: '同意并提交',
        cancelText: '不同意',
        onConfirm: () => {
          const nextValues = { ...values, agreement: true }
          form.setFieldValue('agreement', true)
          submitForm(nextValues)
        },
      }).then()
      return
    }

    submitForm(values)
  }

  return (
    <Stack className="max-w-xl m-auto bg-[#F5F5F5] min-h-screen">
      {/* Header with Banner */}
      <div className="relative w-full overflow-hidden">
        <div
          className="w-full h-[300px] rounded-b-[28px]"
          style={{
            background:
              'radial-gradient(120% 100% at 20% 20%, rgba(255,255,255,0.08) 0%, transparent 60%), linear-gradient(135deg, #121C44 0%, #3A55DD 60%, #5C7BFF 100%)',
          }}
        >
          <div className="absolute inset-0">
            <div className="absolute -top-8 -right-10 w-48 h-48 bg-white/10 blur-3xl rounded-full" />
            <div className="absolute bottom-4 left-6 w-28 h-28 bg-[#6D83FF]/30 blur-2xl rounded-full" />
            <div className="absolute top-8 left-10 w-20 h-20 bg-white/10 blur-xl rounded-full" />
          </div>
          <div
            className="absolute bottom-0 left-0 right-0 h-20"
            style={{
              background: 'linear-gradient(to top, #F5F5F5, transparent)',
            }}
          />
          <div className="absolute top-0 left-0 right-0 px-6 pt-12 pb-10 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs text-white font-semibold tracking-wide backdrop-blur">
                悦享查 · 官方合作伙伴
              </span>
              <span className="px-3 py-1.5 rounded-full border border-white/25 text-[12px] text-white/85 font-medium backdrop-blur">
                资料加密传输 · 安全合规
              </span>
            </div>
            <div className="space-y-3 max-w-2xl">
              <h1 className="text-[30px] leading-[1.2] font-extrabold text-white drop-shadow-sm">
                开启您的财富合伙人之路
              </h1>
              <p className="text-white/80 text-sm leading-relaxed">
                成为悦享查官方代理，享受品牌背书、专属培训与完整的运营支持，放心推广、透明结算。
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/12 border border-white/20 backdrop-blur">
                <IconShieldCheck size={18} className="text-white" />
                <div className="leading-tight">
                  <p className="text-white text-sm font-semibold">实名认证通道</p>
                  <p className="text-white/70 text-[12px]">合规守护收益</p>
                </div>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/12 border border-white/20 backdrop-blur">
                <IconBuildingStore size={18} className="text-white" />
                <div className="leading-tight">
                  <p className="text-white text-sm font-semibold">成长陪跑</p>
                  <p className="text-white/70 text-[12px]">物料与培训支持</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <Stack className="px-3 pb-10 -mt-8 relative z-10 space-y-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0]">
          {/* Form Title */}
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 rounded-lg bg-[#3A55DD]/10">
              <svg className="w-5 h-5 text-[#3A55DD]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </div>
            <span className="text-base font-bold text-[#1E293B]">填写申请信息</span>
          </div>

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                placeholder="请输入真实姓名"
                leftSection={<IconUser size={18} className="text-[#94A3B8]" />}
                classNames={{
                  input:
                    'h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-lg text-[#1E293B] placeholder:text-[#94A3B8] focus:border-[#3A55DD]',
                }}
                {...form.getInputProps('name')}
              />

              <TextInput
                placeholder="请输入身份证号"
                leftSection={<IconId size={18} className="text-[#94A3B8]" />}
                classNames={{
                  input:
                    'h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-lg text-[#1E293B] placeholder:text-[#94A3B8] focus:border-[#3A55DD]',
                }}
                {...form.getInputProps('idCardNo')}
              />

              <TextInput
                placeholder="请输入已注册的手机号"
                leftSection={<IconPhone size={18} className="text-[#94A3B8]" />}
                classNames={{
                  input:
                    'h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-lg text-[#1E293B] placeholder:text-[#94A3B8] focus:border-[#3A55DD]',
                }}
                {...form.getInputProps('phone')}
              />

              <TextInput
                placeholder="请输入验证码"
                maxLength={6}
                leftSection={<IconLock size={18} className="text-[#94A3B8]" />}
                rightSectionWidth={100}
                rightSection={
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={handleSendCode}
                    disabled={isActive}
                    className="hover:bg-transparent"
                    styles={{
                      root: {
                        color: isActive ? '#9CA3AF' : '#3A55DD',
                        fontWeight: 500,
                      },
                    }}
                  >
                    {label}
                  </Button>
                }
                classNames={{
                  input:
                    'h-12 bg-[#F8FAFC] border-[#E2E8F0] rounded-lg text-[#1E293B] placeholder:text-[#94A3B8] focus:border-[#3A55DD]',
                }}
                {...form.getInputProps('smsCode')}
              />

              {/* Agreement Checkbox */}
              <Checkbox
                color="#3A55DD"
                label={<AgreementLabel />}
                {...form.getInputProps('agreement', { type: 'checkbox' })}
              />

              {/* Submit Button */}
              <Button
                loading={loading}
                type="submit"
                fullWidth
                size="md"
                className="mt-2 h-12 text-base font-semibold"
                styles={{
                  root: {
                    backgroundColor: '#3A55DD',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: '#2F45B5',
                    },
                  },
                }}
              >
                立即申请成为合伙人
              </Button>

              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 text-sm text-[#475569] leading-relaxed">
                <div className="flex items-center gap-2 text-[#1E293B] font-semibold">
                  <IconChecklist size={18} className="text-[#3A55DD]" />
                  <span>申请须知</span>
                </div>
                <p className="mt-2">
                  请使用本人实名信息提交，保持电话畅通。审核通过后即可按合伙人规则推广报告和结算收益。
                </p>
              </div>
            </Stack>
          </form>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-[#E2E8F0] space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Benefits</p>
              <h2 className="text-lg font-bold text-[#1E293B]">选择悦享查合伙人</h2>
            </div>
            <div className="px-3 py-1 rounded-full bg-[#EEF2FF] text-[#3A55DD] text-xs font-medium">高品质服务</div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {highlights.map((item) => (
              <div
                key={item.title}
                className="flex gap-3 items-start p-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC]"
              >
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-[#3A55DD] shadow-sm">
                  <item.icon size={20} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#1E293B]">{item.title}</p>
                  <p className="text-xs text-[#475569] mt-1 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-[#E2E8F0] space-y-3">
          <div className="flex items-center gap-2 text-[#1E293B] font-semibold">
            <IconShieldCheck size={18} className="text-[#3A55DD]" />
            <span>温馨提示</span>
          </div>
          <ul className="list-disc pl-4 space-y-2 text-sm text-[#475569] leading-relaxed">
            {warmTips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </div>
      </Stack>
    </Stack>
  )
}
